import { useMemo } from "react";
import { BellReferrer, useBellStore } from "@companion-core/shared/app/Store/bellStore";
import { useConfigStore } from "@companion-core/shared/app/Store/configStore";
import { Category } from "@companion-core/shared/app/Components/Bell/Categories";
import { loadFormFromPath } from "@companion-core/shared/app/Utils/dynamicForm";
import { useWizardStore } from "@companion-core/shared/app/Store/wizardStore";

interface BellLinkProps {
  platform: "mobile" | "web";
  bellReferrer?: BellReferrer;
  nativeBellNavigation?: () => void;
}

export const useBellLink = ({
  platform,
  nativeBellNavigation,
  bellReferrer = "bell",
}: BellLinkProps) => {
  const {
    setBellContent,
    setBellCategory,
    setFormPath,
    clearBell,
    isIntroPopUpVisible,
    setIsIntroPopUpVisible,
    setIsPopUpVisible,
  } = useBellStore((state) => ({
    setBellContent: state.setBellContent,
    setBellCategory: state.setBellCategory,
    setFormPath: state.setFormPath,
    clearBell: state.clearBell,
    setIsPopUpVisible: state.setIsPopUpVisible,
    setIsIntroPopUpVisible: state.setIsIntroPopUpVisible,
    isIntroPopUpVisible: state.isIntroPopUpVisible,
  }));
  const { setCurrentForm } = useWizardStore();
  const { config } = useConfigStore();
  const bellModule = useMemo(() => config?.modules?.concierge?.modules?.bell, [config]);
  const bellHomeEnabled = useMemo(() => bellModule?.options.home.enabled, [bellModule]);
  const bellCategories = useMemo(() => bellModule?.options.categories as Category[], [bellModule]);

  const linkValidation = (dismantledLink: string[]) => {
    const structure = ["home", "categories"];
    for (let i = 0; i < dismantledLink.length && i < 2; i++) {
      if (dismantledLink[i] !== structure[i]) return false;
    }
    return true;
  };

  const openBell = () => {
    switch (platform) {
      case "web":
        isIntroPopUpVisible && setIsIntroPopUpVisible(false);
        setIsPopUpVisible(true);
        break;
      case "mobile":
        nativeBellNavigation && nativeBellNavigation();
        break;
      default:
        break;
    }
  };

  const openBellHome = (dismantledLink: string[]) => {
    if (dismantledLink[0] !== "home" && !bellHomeEnabled) return false;
    clearBell();
    setBellContent("home", true, bellReferrer);
    openBell();
    return true;
  };

  const openBellCategories = (dismantledLink: string[]) => {
    if (dismantledLink[1] !== "categories") return openBellHome(dismantledLink);
    clearBell();
    setBellContent("categories", true, bellReferrer);
    openBell();
    return true;
  };

  const openBellForm = (dismantledLink: string[]) => {
    const category: Category | null =
      bellCategories?.find((category: Category) => {
        if (category.category) return category.category === dismantledLink[2];
        return category.name === dismantledLink[2];
      }) ?? null;
    if (!category) return openBellCategories(dismantledLink);
    clearBell();
    const jsonForm = loadFormFromPath(category.formPath);
    setBellContent("form", false, bellReferrer);
    setBellCategory(category);
    setFormPath(category.formPath);
    setCurrentForm(jsonForm);
    openBell();
    return true;
  };

  const openBellLink = (link: string) => {
    if (!bellModule.enabled || !link) return;
    const dismantledLink = link.split("/");
    if (!linkValidation(dismantledLink)) return;
    switch (dismantledLink.length) {
      case 1:
        openBellHome(dismantledLink);
        break;
      case 2:
        openBellCategories(dismantledLink);
        break;
      case 3:
        openBellForm(dismantledLink);
        break;
      default:
        break;
    }
  };

  return { openBellLink };
};
